import React, { useState } from 'react';
import { RcFile } from 'antd/lib/upload';
import { useSetRecoilState } from 'recoil';

import DependenciesModal from '../DependenciesModal';
import ProgressPanel from '../ProgressPanel';
import { ELMA365ToolsetModel, ELMA365VersionModel, ExchangeDependency, ExchangeObjectType } from '../../../gamma-api';
import { defaultApi } from '../../apis';
import IbIcon from '../common/IbIcon';
import { alertsSelectorAdd } from '../../recoil';
import { getTranslateFunc } from '../../utils/stringUtil';
import FileUpload from '../common/FileUpload';

import { buildDependenciesFile, hasActiveDependencies } from './utils';

interface IToolkitImportProps {
  folderId: string;
  toolsetId?: string;
  onFinish?: (toolset: ELMA365ToolsetModel, version: ELMA365VersionModel) => Promise<void>;
}

const ToolkitImport: React.FC<IToolkitImportProps> = ({ folderId, toolsetId, onFinish, children }) => {
  const translate = getTranslateFunc('Toolkits: ELMA365: Import:');

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [currentFiles, setCurrentFiles] = useState<RcFile[]>([]);
  const [currentDependencies, setCurrentDependencies] = useState<ExchangeDependency[]>([]);

  const [dependenciesModalVisible, setDependenciesModalVisible] = useState(false);
  const [progressPanelVisible, setProgressPanelVisible] = useState(false);

  const importToolset = async (file: RcFile, dependencies: ExchangeDependency[]) => {
    const response = toolsetId
      ? await defaultApi.importELMA365ToolkitToolsetUpdateForm(
          toolsetId,
          folderId,
          file,
          buildDependenciesFile(dependencies)
        )
      : await defaultApi.importELMA365ToolkitToolsetCreateForm(folderId, file, buildDependenciesFile(dependencies));
    onFinish && (await onFinish(response.data.toolset, response.data.version));
  };

  const onFileUpload = async (files: RcFile[]) => {
    setProgressPanelVisible(true);
    try {
      const file = files[0];
      const response = toolsetId
        ? await defaultApi.inspectELMA365ToolkitToolsetUpdateForm(toolsetId, folderId, file)
        : await defaultApi.inspectELMA365ToolkitToolsetCreateForm(folderId, file);
      const dependencies = response.data.dependencies;
      if (hasActiveDependencies(dependencies, 1)) {
        setCurrentFiles(files);
        setCurrentDependencies(dependencies);
        setDependenciesModalVisible(true);
      } else {
        await importToolset(file, dependencies);
      }
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
    }
    return false;
  };

  const onImportFinish = async () => {
    setDependenciesModalVisible(false);
    setProgressPanelVisible(true);
    try {
      await importToolset(currentFiles[0], currentDependencies);
    } catch (e) {
      addAlert({
        content: translate('Error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      return;
    } finally {
      setProgressPanelVisible(false);
      setCurrentDependencies([]);
      setCurrentFiles([]);
    }

    addAlert({
      content: translate('Success'),
      type: 'success',
    });
  };

  const onImportCancel = async () => {
    setDependenciesModalVisible(false);
    setCurrentDependencies([]);
    setCurrentFiles([]);
  };

  const onDependenciesChange = (dependencies: ExchangeDependency[]) => {
    setCurrentDependencies(dependencies);
  };

  return (
    <>
      <FileUpload noButton accept=".zip,.json" disabled={false} multiple={false} onUpload={onFileUpload}>
        {children}
      </FileUpload>
      <DependenciesModal
        dependencies={currentDependencies}
        entityId={toolsetId}
        entityType={ExchangeObjectType.Toolkit}
        folderId={folderId}
        visible={dependenciesModalVisible}
        onCancel={onImportCancel}
        onChange={onDependenciesChange}
        onFinish={onImportFinish}
      />
      <ProgressPanel.ToolkitImport visible={progressPanelVisible} />
    </>
  );
};

export default ToolkitImport;
